import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Gamepad2, Sparkles, Brain, Hash, Grid } from 'lucide-react';
import Button from '../components/ui/Button';
import GameCard from '../components/GameCard';
import { games as gamesApi } from '../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await gamesApi.getGamesByCategory();
      setGames(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch games:', error);
      setLoading(false);
    }
  };

  // Filter free games based on their IDs
  const getFreeGames = () => {
    const freeGameIds = ['1.1', '1.2', '1.3', '1.4']; // 2048, Memory Match, XO Game, Sudoku
    return games
      .flatMap(category => category.games)
      .filter(game => freeGameIds.includes(game.id));
  };

  const handleGameClick = (game) => {
    switch (game.id) {
      case '1.1': // 2048
        navigate('/games/2048');
        break;
      case '1.2': // Memory Match
        navigate('/games/MemoryMatch');
        break;
      case '1.3': // XOGame
        navigate('/games/XOGame');
        break;
      case '1.4': // Sudoku
        navigate('/games/SudokuGame');
        break;
        case '1.5': // Chess
        navigate('/games/chess');
        break;
      default:
        navigate(`/library`);
    }
  };

  // Map game IDs to icons
  const getGameIcon = (gameId) => {
    const icons = {
      '1.1': <Grid className="w-8 h-8 text-purple-500" />,
      '1.2': <Brain className="w-8 h-8 text-blue-500" />,
      '1.3': <Hash className="w-8 h-8 text-green-500" />,
      '1.4': <Gamepad2 className="w-8 h-8 text-pink-500" />
    };
    return icons[gameId];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-purple-900 flex items-center justify-center">
        <p className="text-white text-xl">Loading games...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-purple-900">
    {/* Welcome */}
      <section className="py-20 w-full snap-start relative overflow-hidden flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-blue-50 to-white"></div>
          <div className="w-full relative z-10">
            <div className="flex items-center justify-center h-full">
              <div className="max-w-3xl">
                <div className="flex items-center gap-4 mb-6 justify-center">
                  <Gamepad2 className="w-12 h-12 text-purple-600 animate-bounce" />
                  <h2 className="text-6xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Welcome to Stray Games.
                  </h2>
                </div>
                <p className="text-2xl mb-8 text-gray-700 text-center">
                Play your favorite games instantly, anywhere, anytime.
                Jump right into our collection of free games - no login required!
                  <Sparkles className="inline ml-2 w-6 h-6 text-yellow-500 animate-pulse" />
                </p>
                <div className="text-center">
                  <Link to="/login" 
                    className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 px-8 py-4 rounded-lg text-xl font-bold text-white
                    transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/50">
                    Browse All Games
                  </Link>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* Free Games Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="py-12 text-5xl font-bold text-white mb-8 text-center">
          <Sparkles className="inline ml-2 w-6 h-6 text-yellow-500 animate-pulse" /> Free Games
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {getFreeGames().map((game) => (
            <GameCard
              key={game.id}
              game={game}
              icon={getGameIcon(game.id)}
              onClick={() => handleGameClick(game)}
            />
          ))}
        </div>
      </div>

      {/* Premium Features Teaser */}
      <div className="max-w-6xl mx-auto px-4 pb-16 text-center">
        <h2 className="text-2xl font-bold text-white mb-4">
          Want More Games?
        </h2>
        <p className="text-gray-300 mb-6">
          Sign up to access our full library of premium games and features
        </p>
        <div className="flex justify-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/login')}
            className="border-white/20 text-white hover:bg-white/10"
          >
            Login
          </Button>
          <Button 
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
          >
            Register Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;